import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import SimulationResultModal from '@/components/modals/SimulationResultModal';
import DataRow from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	useCancelPurchaseOrder,
	useExtendPurchaseOrder,
	useGetPurchaseOrderActionDetail,
	usePrintPurchaseOrder,
	useRetrievePurchaseOrder,
	useSubmitDisbursementPurchaseOrder,
	useSubmitPurchaseOrder,
} from '@/libs/fleet-api/hooks/purchase-order';
import {
	CancelPurchaseOrderRequest,
	ExtendPurchaseOrderRequest,
	RetrievePurchaseOrderRequest,
	SubmitPurchaseOrderParams,
} from '@/libs/fleet-api/services/purchase-order';
import { FileData } from '@/libs/fleet-api/services/types';
import { uploader } from '@/libs/fleet-api/uploader';
import {
	ArrowLeftOutlined,
	ArrowRightOutlined,
	CheckCircleFilled,
	InfoCircleFilled,
	RetweetOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
	Alert,
	Button,
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Space,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import { useWatch } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';

const searchByOptions = [
	{ id: 'poReleaseDate', value: 'Tanggal Terbit PO' },
	{ id: 'poExpiredDate', value: 'Tanggal PO Expired' },
	{ id: 'poStatus', value: 'Status' },
];

const pageSize = 20;

const FRMPOActionListPage: FC<{
	mppId: number;
	mppNo: string;
	spfNo: string;
	mklNo: string;
}> = ({ mppId, spfNo, mklNo, mppNo }) => {
	const { navigate } = useApp();
	const [searchBy, setSearchBy] = useState<string>(searchByOptions[0].id);
	const [searchValue, setSearchValue] = useState<string>('');
	const [page, setPage] = useState(1);
	const [triggeredSearch, setTriggeredSearch] = useState<{
		by: string;
		value: string;
	} | null>(null);
	const [statusOptions, setStatusOptions] = useState<
		{ id: string; value: string }[]
	>([]);

	// Prepare params for API call
	const params: {
		pagination: { pageSize: number; pageNumber: number };
		mppId?: number;
		poReleaseDate?: string;
		poExpiredDate?: string;
		poStatus?: string;
	} = {
		pagination: { pageSize, pageNumber: page },
		mppId,
		...(triggeredSearch &&
		triggeredSearch.by === 'poReleaseDate' &&
		triggeredSearch.value
			? { poReleaseDate: triggeredSearch.value }
			: {}),
		...(triggeredSearch &&
		triggeredSearch.by === 'poExpiredDate' &&
		triggeredSearch.value
			? { poExpiredDate: triggeredSearch.value }
			: {}),
		...(triggeredSearch &&
		triggeredSearch.by === 'poStatus' &&
		triggeredSearch.value
			? { poStatus: triggeredSearch.value }
			: {}),
	};

	const { data, isLoading, error, refetch } =
		useGetPurchaseOrderActionDetail(params);

	// Update status options from API response
	useEffect(() => {
		if (data?.options?.statusOptions) {
			setStatusOptions(data.options.statusOptions);
		}
	}, [data?.options?.statusOptions]);

	// Reset page to 1 on new search
	useEffect(() => {
		if (triggeredSearch) setPage(1);
	}, [triggeredSearch]);

	const handleSearch = () => {
		setTriggeredSearch({ by: searchBy, value: searchValue });
		setPage(1);
	};

	// Table data
	const tableData = (data?.details || []).map((item, idx) => ({
		key: idx + 1 + (page - 1) * pageSize,
		no: idx + 1 + (page - 1) * pageSize,
		noMkl: mklNo,
		noSpf: spfNo,
		spfId: item.spfId,
		facilityId: item.facilityId,
		creditSimulationId: item.creditSimulationId,
		creditSimulationNo: item.creditSimulationNo,
		purchaseOrderId: item.purchaseOrderId,
		noMpp: data?.mppNo,
		noPo: item.purchaseOrderNo,
		applicationId: item.applicationId,
		noAgreement: '-', // Not in API, placeholder
		supplier: item.supplierName,
		merkAsset: item.brand,
		ntf: item.ntfAmount,
		tglTerbitPo: item.purchaseOrderReleaseDate,
		tglPoExpired: item.purchaseOrderExpiredDate,
		tglPerpanjanganPo: '-', // Not in API, placeholder
		status: item.status,
		totalUnit: item.totalUnit,
		allowCreatePo: item.allowCreatePo,
		allowSelect: item.allowSelect,
		allowRetrieve: item.allowRetrieve,
		allowExtend: item.allowExtend,
	}));

	// Dynamic input for search value
	let searchValueInput = null;
	if (searchBy === 'poReleaseDate' || searchBy === 'poExpiredDate') {
		searchValueInput = (
			<DatePicker
				value={searchValue ? dayjs(searchValue) : null}
				onChange={(date) =>
					setSearchValue(date ? date.add(7, 'hour').toISOString() : '')
				}
				style={{ borderRadius: 8, width: '40%' }}
				format={{ format: 'DD-MM-YYYY', type: 'mask' }}
			/>
		);
	} else if (searchBy === 'poStatus') {
		searchValueInput = (
			<Select
				style={{ minWidth: 160, width: '40%' }}
				value={searchValue}
				onChange={setSearchValue}
				options={statusOptions.map((opt) => ({
					label: opt.value,
					value: opt.id,
				}))}
				allowClear
				placeholder="Pilih status..."
			/>
		);
	} else {
		searchValueInput = (
			<Input
				placeholder="Masukkan kata kunci..."
				value={searchValue}
				onChange={(e) => setSearchValue(e.target.value)}
				onPressEnter={handleSearch}
				style={{ borderRadius: 8, width: '40%' }}
				allowClear
			/>
		);
	}
	const [createdPONumbers, setCreatedPONumbers] = useState<{
		purchaseOrderNo: string;
		plakatNo: string;
	}>();

	const { form: formCreatePO, fieldName: fieldNameCreatePO } =
		useForm<SubmitPurchaseOrderParams>();
	const { form: formRetrievePO, fieldName: fieldNameRetrievePO } =
		useForm<RetrievePurchaseOrderRequest>();
	const { form: formExtendPO, fieldName: fieldNameExtendPO } =
		useForm<ExtendPurchaseOrderRequest>();
	const { form: formCancelPO, fieldName: fieldNameCancelPO } =
		useForm<CancelPurchaseOrderRequest>();

	const POCreationId = useWatch(
		fieldNameCreatePO('creditSimulationId'),
		formCreatePO,
	);
	const PORetrievalId = useWatch(
		fieldNameRetrievePO('purchaseOrderId'),
		formRetrievePO,
	);
	const POExtensionId = useWatch(
		fieldNameExtendPO('purchaseOrderId'),
		formExtendPO,
	);
	const POCancellationId = useWatch(
		fieldNameCancelPO('purchaseOrderId'),
		formCancelPO,
	);

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	const [extendCancelPOId, setExtendCancelPOId] = useState<number | null>(null);

	const submitPurchaseOrderMutate = useSubmitPurchaseOrder();
	const { mutateAsync: retrievePurchaseOrderMutate } =
		useRetrievePurchaseOrder();
	const { mutateAsync: extendPurchaseOrderMutate } = useExtendPurchaseOrder();
	const { mutateAsync: cancelPurchaseOrderMutate } = useCancelPurchaseOrder();

	const [selectedPOs, setSelectedPOs] = useState<number[]>([]);
	const [POOperationSuccessModal, setPOOperationSuccessModal] = useState('');

	// Simulation modal state
	const [simulationModalOpen, setSimulationModalOpen] = useState(false);
	const [selectedSimulation, setSelectedSimulation] = useState<{
		simulationId?: number;
		spfId?: number;
		facilityId?: number;
		calculationTypeId?: number;
	} | null>(null);

	const handleCloseSimulationModal = () => {
		setSimulationModalOpen(false);
		setSelectedSimulation(null);
	};

	const handleCreatePO = (
		creditSimulationId: number,
		purchaseOrderId: number,
	) => {
		formCreatePO.setFieldsValue({
			creditSimulationId: creditSimulationId,
			purchaseOrderId: purchaseOrderId,
			unit:
				data?.details.find(
					(detail) =>
						detail.creditSimulationId === creditSimulationId &&
						detail.purchaseOrderNo === null,
				)?.remainingUnit || 1,
		});
	};

	const handleClosePOCreationModal = () => {
		formCreatePO.setFieldValue(fieldNameCreatePO('creditSimulationId'), null);
	};

	const onFormFinishCreatePO = async (
		formValues: SubmitPurchaseOrderParams,
	) => {
		// console.log(formValues);

		// Prevent double submission
		if (submitPurchaseOrderMutate.isPending) {
			console.warn('Purchase order submission already in progress');
			return;
		}

		try {
			const createdNumbers =
				await submitPurchaseOrderMutate.mutateAsync(formValues);
			setCreatedPONumbers(createdNumbers);
			// alert('submitPurchaseOrderMutate');
			refetch();
		} catch (error) {
			console.error('Failed to create purchase order:', error);
			// Don't close modal on error to allow user to retry
			return;
		}

		// Only close modal on success
		handleClosePOCreationModal();
	};

	const handleRetrieve = (purchaseOrderId: number) => {
		formRetrievePO.setFieldValue(
			fieldNameRetrievePO('purchaseOrderId'),
			purchaseOrderId,
		);
	};

	const onFormFinishRetrievePO = async (
		formValues: RetrievePurchaseOrderRequest,
	) => {
		try {
			await retrievePurchaseOrderMutate(formValues);
			formRetrievePO.setFieldValue(
				fieldNameRetrievePO('purchaseOrderId'),
				null,
			);
			refetch();
			setPOOperationSuccessModal(`PO berhasil di-retrieve!`);
		} catch (error) {
			console.error(error);
		}
	};

	const handleClosePORetrievalModal = () => {
		formRetrievePO.setFieldValue(fieldNameRetrievePO('purchaseOrderId'), null);
	};

	const handleClosePOOperationSuccessModal = () =>
		setPOOperationSuccessModal('');

	const handleExtendCancelModal = (purchaseOrderId: number) => {
		setExtendCancelPOId(purchaseOrderId);
	};

	const handleExtendPOModal = () => {
		formExtendPO.setFieldValue(
			fieldNameExtendPO('purchaseOrderId'),
			extendCancelPOId,
		);
	};

	const handleClosePOExtensionCancelModal = () => setExtendCancelPOId(null);
	const handleClosePOExtensionModal = () => {
		formExtendPO.setFieldValue(fieldNameExtendPO('purchaseOrderId'), null);
		handleClosePOExtensionCancelModal();
	};

	const onFormFinishExtendPO = async (
		formValues: ExtendPurchaseOrderRequest,
	) => {
		try {
			await extendPurchaseOrderMutate(formValues);
			formExtendPO.setFieldValue(fieldNameExtendPO('purchaseOrderId'), null);
			refetch();
			setPOOperationSuccessModal('PO berhasil diperpanjang!');
			handleClosePOExtensionModal();
		} catch (error) {
			console.error(error);
		}
	};

	const handleCancelPOModal = () => {
		formCancelPO.setFieldValue(
			fieldNameCancelPO('purchaseOrderId'),
			extendCancelPOId,
		);
	};

	const handleClosePOCancellationModal = () => {
		formCancelPO.setFieldValue(fieldNameCancelPO('purchaseOrderId'), null);
		handleClosePOExtensionCancelModal();
	};

	const onFormFinishCancelPO = async (
		formValues: CancelPurchaseOrderRequest,
	) => {
		try {
			await cancelPurchaseOrderMutate(formValues);
			formCancelPO.setFieldValue(fieldNameCancelPO('purchaseOrderId'), null);
			setPOOperationSuccessModal('PO berhasil di-cancel!');
			handleClosePOCancellationModal();
		} catch (error) {
			console.error(error);
		} finally {
			refetch();
		}
	};

	const [requestPencairanModalOpen, setRequestPencairanModalOpen] =
		useState(false);

	const { mutateAsync: submitDisbursementPurchaseOrderMutate } =
		useSubmitDisbursementPurchaseOrder();

	const { mutateAsync: printPurchaseOrderMutate } = usePrintPurchaseOrder();

	const { openDocumentModal, openCustomerDetail, openCustomerGroupDetail } =
		useModals();

	const handleCloseRequestPencairanModal = () =>
		setRequestPencairanModalOpen(false);

	const submitDisbursementRequest = async () => {
		try {
			await submitDisbursementPurchaseOrderMutate({
				purchaseOrderIds: selectedPOs,
			});
			setSelectedPOs([]);
		} catch (error) {
			console.error(error);
		} finally {
			setRequestPencairanModalOpen(false);
			refetch();
		}
	};

	const [printAllPurchaseOrderModal, setPrintAllPurchaseOrderModal] =
		useState(false);

	const printAllPurchaseOrder = async () => {
		try {
			const response = await printPurchaseOrderMutate({
				purchaseOrderIds: selectedPOs,
			});
			openDocumentModal(response);
		} catch (error) {
			console.error(error);
		}
	};

	const handlePrintPOSingle = async () => {
		try {
			const response = await printPurchaseOrderMutate({
				purchaseOrderIds: [
					data?.details.find(
						(detail) =>
							detail.purchaseOrderNo === createdPONumbers?.purchaseOrderNo,
					)?.purchaseOrderId ?? 0,
				],
			});
			openDocumentModal(response);
			setCreatedPONumbers(undefined);
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<>
			{/* PO CREATION MODAL */}
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={formCreatePO}
				layout="horizontal"
				onFinish={onFormFinishCreatePO}
				scrollToFirstError
			>
				<Form.Item
					name={fieldNameCreatePO('purchaseOrderId')}
					initialValue={
						data?.details.find(
							(detail) => detail.creditSimulationId === POCreationId,
						)?.purchaseOrderId ?? null
					}
					hidden
				>
					<Input />
				</Form.Item>
				<FleetModal
					open={!!POCreationId}
					footer={null}
					closable={false}
					maskClosable={false}
					width={600}
					style={{
						padding: '36px 32px 24px 32px',
						borderRadius: 12,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						width: '100%',
						textAlign: 'center',
					}}
				>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<InfoCircleFilled
							style={{ fontSize: 48, color: '#1557BC', marginBottom: 16 }}
						/>
					</div>
					<div style={{ fontWeight: 600, fontSize: 18, marginBottom: 32 }}>
						Silahkan input jumlah unit yang akan di-cetak PO
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
						<Form.Item
							name={fieldNameCreatePO('unit')}
							label="Pilih Jumlah Unit"
							required
						>
							<Select
								options={Array.from(
									{
										length: Math.max(
											0,
											data?.details.find(
												(detail) =>
													detail.creditSimulationId === POCreationId &&
													(detail.purchaseOrderNo === null ||
														detail.purchaseOrderNo === ''),
											)?.remainingUnit ?? 0,
										),
									},
									(_, i) => ({ value: i + 1, label: String(i + 1) }),
								)}
								defaultValue={
									data?.details.find(
										(detail) =>
											detail.creditSimulationId === POCreationId &&
											detail.purchaseOrderNo === null,
									)?.remainingUnit || 1
								}
							/>
						</Form.Item>
						<div style={{ display: 'flex', gap: 8, alignItems: 'start' }}>
							<Form.Item name={fieldNameCreatePO('isIncludedCostComponent')}>
								<input
									type="checkbox"
									hidden
								/>
							</Form.Item>
							<Checkbox
								style={{ fontSize: 24 }}
								onChange={(value) => {
									// console.log(value);
									formCreatePO.setFieldValue(
										fieldNameCreatePO('isIncludedCostComponent'),
										value.target.checked,
									);
								}}
							/>
							<span style={{ textAlign: 'left', fontSize: 12 }}>
								Centang apabila komponen biaya lainnya (Biaya Admin, Biaya
								Fidusia, Provisi, Biaya Layanan, Asuransi, Angsuran Pertama-jika
								ada) diikutsertakan pada Purchase Order yang ingin dicetak
							</span>
						</div>

						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								gap: 16,
								width: '100%',
								marginBottom: 24,
							}}
						>
							<Button
								onClick={handleClosePOCreationModal}
								type="default"
								style={{
									minWidth: 120,
									fontWeight: 600,
									fontSize: 16,
									borderRadius: 6,
									border: '1px solid #1557BC',
									color: '#1557BC',
									background: 'white',
								}}
							>
								BACK
							</Button>
							<Button
								htmlType="submit"
								onClick={formCreatePO.submit}
								type="primary"
								style={{
									minWidth: 120,
									fontWeight: 600,
									fontSize: 16,
									borderRadius: 6,
									background: '#1557BC',
									color: 'white',
									border: 'none',
								}}
								loading={submitPurchaseOrderMutate.isPending}
								disabled={submitPurchaseOrderMutate.isPending}
							>
								SUBMIT
							</Button>
						</div>
					</div>
				</FleetModal>
				<Form.Item
					hidden
					name={fieldNameCreatePO('creditSimulationId')}
				>
					<Input />
				</Form.Item>
			</Form>

			{/* PO RETRIEVAL MODAL */}
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={formRetrievePO}
				layout="vertical"
				onFinish={onFormFinishRetrievePO}
				scrollToFirstError
			>
				<FleetModal
					open={!!PORetrievalId}
					footer={null}
					closable={false}
					maskClosable={false}
					width={500}
					style={{
						padding: '36px 32px 24px 32px',
						borderRadius: 12,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						width: '100%',
						textAlign: 'center',
					}}
				>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<InfoCircleFilled
							style={{ fontSize: 48, color: '#1557BC', marginBottom: 16 }}
						/>
					</div>
					<div style={{ fontWeight: 600, fontSize: 18, marginBottom: 32 }}>
						Apakah anda ingin melakukan Retrieve PO dengan detail berikut?
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
						<div>
							<Divider />

							{(() => {
								const record = data?.details.find(
									(detail) => detail.purchaseOrderId === PORetrievalId,
								);
								return (
									<>
										<Row>
											<Col span={8}>No. PO</Col>
											<Col
												span={16}
												style={{
													display: 'flex',
													justifyContent: 'flex-start',
													fontWeight: 700,
												}}
											>
												: {record?.purchaseOrderNo}
											</Col>
										</Row>
										<Row>
											<Col span={8}>Merk Asset</Col>
											<Col
												span={16}
												style={{
													display: 'flex',
													justifyContent: 'flex-start',
													fontWeight: 700,
												}}
											>
												: {record?.brand}
											</Col>
										</Row>
										<Row>
											<Col span={8}>Jumlah Unit</Col>
											<Col
												span={16}
												style={{
													display: 'flex',
													justifyContent: 'flex-start',
													fontWeight: 700,
												}}
											>
												: {record?.totalUnit || 0}
											</Col>
										</Row>
									</>
								);
							})()}
							<Divider />
						</div>
						<Form.Item
							name={fieldNameRetrievePO('notes')}
							label="Notes"
							labelCol={{ span: 24 }}
						>
							<TextArea />
						</Form.Item>
						<Divider style={{ opacity: 0, marginBottom: 48 }} />
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								gap: 16,
								width: '100%',
								marginBottom: 24,
							}}
						>
							<Button
								onClick={handleClosePORetrievalModal}
								type="default"
								style={{
									minWidth: 120,
									fontWeight: 600,
									fontSize: 16,
									borderRadius: 6,
									border: '1px solid #1557BC',
									color: '#1557BC',
									background: 'white',
								}}
							>
								BACK
							</Button>
							<Button
								htmlType="submit"
								type="primary"
								style={{
									minWidth: 120,
									fontWeight: 600,
									fontSize: 16,
									borderRadius: 6,
									background: '#1557BC',
									color: 'white',
									border: 'none',
								}}
								onClick={formRetrievePO.submit}
							>
								SUBMIT
							</Button>
						</div>
					</div>
				</FleetModal>
				<Form.Item
					hidden
					name={fieldNameRetrievePO('purchaseOrderId')}
				>
					<Input />
				</Form.Item>
			</Form>

			{/* PO CREATION SUCCESS MODAL */}
			<FleetModal
				open={!!createdPONumbers}
				closable={true}
				maskClosable={false}
				onCancel={() => setCreatedPONumbers(undefined)}
				width={600}
				onOk={handlePrintPOSingle}
				okText="PRINT PO"
				style={{
					padding: '36px 32px 24px 32px',
					borderRadius: 12,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					width: '100%',
					textAlign: 'center',
				}}
				cancelButtonProps={{ style: { display: 'none' } }}
			>
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<CheckCircleFilled
						style={{ fontSize: 48, color: '#48A11E', marginBottom: 16 }}
					/>
				</div>
				<div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
					Purchase Order Berhasil Dibuat!
				</div>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<p style={{ color: '#9C9C9C', fontSize: 16 }}>
						Berikut adalah nomor Purchase Order Debitur Anda:
					</p>
					<h1 style={{ marginTop: -8 }}>{createdPONumbers?.purchaseOrderNo}</h1>
					{createdPONumbers?.plakatNo?.length && (
						<span
							style={{
								background: '#D6E6FF',
								paddingLeft: 50,
								paddingRight: 50,
								paddingTop: 10,
								color: '#1557BC',
								paddingBottom: 10,
								fontWeight: 700,
							}}
						>
							Plakat sudah terbentuk dengan nomor {createdPONumbers?.plakatNo}
						</span>
					)}
				</div>
			</FleetModal>

			{/* PO OPERATION SUCCESS MODAL */}
			<FleetModal
				open={POOperationSuccessModal.length > 0}
				closable={false}
				maskClosable={false}
				width={600}
				onOk={handleClosePOOperationSuccessModal}
				okText="OK"
				style={{
					padding: '36px 32px 24px 32px',
					borderRadius: 12,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					width: '100%',
					textAlign: 'center',
				}}
				cancelButtonProps={{ style: { display: 'none' } }}
			>
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<CheckCircleFilled
						style={{ fontSize: 48, color: '#48A11E', marginBottom: 16 }}
					/>
				</div>
				<div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
					{POOperationSuccessModal}
				</div>
			</FleetModal>

			{/* PO EXTEND CANCEL OPTION MODAL */}
			<FleetModal
				open={!!extendCancelPOId}
				maskClosable={true}
				width={400}
				footer={null}
				onCancel={handleClosePOExtensionCancelModal}
				style={{
					padding: '36px 32px 24px 32px',
					borderRadius: 12,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					width: '100%',
					textAlign: 'center',
				}}
			>
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<InfoCircleFilled
						style={{ fontSize: 48, color: '#1557BC', marginBottom: 16 }}
					/>
				</div>
				<div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
					Silahkan pilih kondisi Purchase Order berikut ini:
				</div>
				<Divider style={{ opacity: 0, marginBottom: 24 }} />
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						gap: 16,
						width: '100%',
						marginBottom: 24,
					}}
				>
					<Button
						onClick={handleCancelPOModal}
						type="default"
						style={{
							minWidth: 120,
							fontWeight: 600,
							fontSize: 16,
							borderRadius: 6,
							border: '1px solid #1557BC',
							color: '#1557BC',
							background: 'white',
						}}
					>
						CANCEL PO
					</Button>
					<Button
						htmlType="submit"
						type="primary"
						style={{
							minWidth: 120,
							fontWeight: 600,
							fontSize: 16,
							borderRadius: 6,
							background: '#1557BC',
							color: 'white',
							border: 'none',
						}}
						onClick={handleExtendPOModal}
					>
						PERPANJANG PO
					</Button>
				</div>
			</FleetModal>

			{/* PO EXTENSION MODAL */}
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={formExtendPO}
				layout="horizontal"
				onFinish={onFormFinishExtendPO}
				scrollToFirstError
			>
				<FleetModal
					open={!!POExtensionId}
					footer={null}
					closable={false}
					maskClosable={false}
					width={1000}
					style={{
						padding: '36px 32px 24px 32px',
						borderRadius: 12,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						width: '100%',
						textAlign: 'center',
					}}
				>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
						<FleetTitle>Perpanjangan Purchase Order (PO)</FleetTitle>
						<Alert
							message="Mohon lampirkan dokumen berikut untuk proses perpanjangan purchase
							order. Jika terdapat perubahan skema, jenis dan merk asset,
							silakan ajukan ulang Simulasi Kredit."
							type="info"
							className="custom-alert"
							showIcon
							style={{ marginBottom: 20, fontSize: 12, color: '#1557BC' }}
						/>
						<Row
							gutter={[40, 40]}
							style={{ paddingBottom: 48 }}
						>
							<Col span={12}>
								<>
									<Form.Item
										name={fieldNameExtendPO(['extendRequestDocument', 'id'])}
										noStyle
										label="Surat Permohonan Perpanjangan PO"
									/>
									<Form.Item
										name={fieldNameExtendPO(['extendRequestDocument'])}
										label="Surat Permohonan Perpanjangan PO"
										rules={[
											{
												required: true,
												message: `Surat Permohonan Perpanjangan PO wajib diisi`,
											},
										]}
										getValueProps={(value) => {
											const file = value as FileData;
											return {
												fileList: file?.id
													? ([
															{
																uid: file.id,
																name: file.name,
																thumbUrl: file.url,
															},
														] as UploadFile[])
													: [],
											};
										}}
										normalize={({ file }: { file: UploadFile }) => ({
											id: file.uid,
											format: file.type,
											name: file.name,
											url: file.thumbUrl,
										})}
									>
										<Upload.Dragger
											listType="picture"
											accept=".pdf, .jpg, .jpeg, .png"
											maxCount={1}
											disabled={
												(
													uploadMutation as unknown as {
														isPending: boolean;
														mutateAsync: (args: {
															file: File;
															fileid: string;
															filepath: string;
														}) => Promise<string>;
													}
												).isPending
											} // TODO: type uploadMutation properly
											beforeUpload={() => false}
											onRemove={() => {
												formExtendPO.setFieldValue(
													fieldNameExtendPO(['extendRequestDocument']),
													null,
												);
												return false;
											}}
											onChange={async ({ file }) => {
												const fileid = Date.now().toString();
												const filepath = `customer/${data?.customerId}/${data?.details.find((detail) => detail.purchaseOrderId === extendCancelPOId)}/purchase-order`;
												try {
													const url = await (
														uploadMutation as unknown as {
															isPending: boolean;
															mutateAsync: (args: {
																file: File;
																fileid: string;
																filepath: string;
															}) => Promise<string>;
														}
													).mutateAsync({
														// TODO: type uploadMutation properly
														file: file as unknown as File,
														fileid,
														filepath,
													});
													formExtendPO.setFieldValue(
														fieldNameExtendPO('extendRequestDocument'),
														{
															format: file.type,
															id: fileid,
															name: file.name,
															path: filepath,
															url,
														},
													);
												} catch {
													formExtendPO.setFieldValue(
														fieldNameExtendPO('extendRequestDocument'),
														null,
													);
												}
											}}
										>
											<UploadOutlined
												style={{ fontSize: 12, display: 'block' }}
											/>
										</Upload.Dragger>
									</Form.Item>
								</>
							</Col>
							<Col span={12}>
								<>
									<Form.Item
										name={fieldNameExtendPO(['purchaseOrderDocument', 'id'])}
										noStyle
										label="PO Awal"
									/>
									<Form.Item
										name={fieldNameExtendPO(['purchaseOrderDocument'])}
										label="PO Awal"
										rules={[
											{
												required: true,
												message: `PO Awal wajib diisi`,
											},
										]}
										getValueProps={(value) => {
											const file = value as FileData;
											return {
												fileList: file?.id
													? ([
															{
																uid: file.id,
																name: file.name,
																thumbUrl: file.url,
															},
														] as UploadFile[])
													: [],
											};
										}}
										normalize={({ file }: { file: UploadFile }) => ({
											id: file.uid,
											format: file.type,
											name: file.name,
											url: file.thumbUrl,
										})}
									>
										<Upload.Dragger
											listType="picture"
											accept=".pdf, .doc, .docx"
											maxCount={1}
											disabled={
												(
													uploadMutation as unknown as {
														isPending: boolean;
														mutateAsync: (args: {
															file: File;
															fileid: string;
															filepath: string;
														}) => Promise<string>;
													}
												).isPending
											}
											beforeUpload={() => false}
											onRemove={() => {
												formExtendPO.setFieldValue(
													fieldNameExtendPO(['purchaseOrderDocument']),
													null,
												);
												return false;
											}}
											onChange={async ({ file }) => {
												const fileid = Date.now().toString();
												const filepath = `customer/${data?.customerId}/${data?.details.find((detail) => detail.purchaseOrderId === extendCancelPOId)}/purchase-order`;
												try {
													const url = await (
														uploadMutation as unknown as {
															isPending: boolean;
															mutateAsync: (args: {
																file: File;
																fileid: string;
																filepath: string;
															}) => Promise<string>;
														}
													).mutateAsync({
														// TODO: type uploadMutation properly
														file: file as unknown as File,
														fileid,
														filepath,
													});
													formExtendPO.setFieldValue(
														fieldNameExtendPO('purchaseOrderDocument'),
														{
															format: 'jpeg',
															id: fileid,
															name: file.name,
															path: filepath,
															url,
														},
													);
												} catch {
													formExtendPO.setFieldValue(
														fieldNameExtendPO('purchaseOrderDocument'),
														null,
													);
												}
											}}
										>
											<UploadOutlined
												style={{ fontSize: 12, display: 'block' }}
											/>
										</Upload.Dragger>
									</Form.Item>
								</>
							</Col>
						</Row>

						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								gap: 16,
								width: '100%',
								marginBottom: 24,
							}}
						>
							<Button
								onClick={handleClosePOExtensionModal}
								type="default"
								style={{
									minWidth: 120,
									fontWeight: 600,
									fontSize: 16,
									borderRadius: 6,
									border: '1px solid #1557BC',
									color: '#1557BC',
									background: 'white',
								}}
							>
								BACK
							</Button>
							<Button
								htmlType="submit"
								type="primary"
								style={{
									minWidth: 120,
									fontWeight: 600,
									fontSize: 16,
									borderRadius: 6,
									background: '#1557BC',
									color: 'white',
									border: 'none',
								}}
								onClick={formExtendPO.submit}
							>
								SUBMIT
							</Button>
						</div>
					</div>
				</FleetModal>
				<Form.Item
					hidden
					name={fieldNameRetrievePO('purchaseOrderId')}
				>
					<Input />
				</Form.Item>
			</Form>

			{/* PO CANCELLATION MODAL */}
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={formCancelPO}
				layout="vertical"
				onFinish={onFormFinishCancelPO}
				scrollToFirstError
			>
				<FleetModal
					open={!!POCancellationId}
					footer={null}
					closable={false}
					maskClosable={false}
					width={500}
					style={{
						padding: '36px 32px 24px 32px',
						borderRadius: 12,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						width: '100%',
						textAlign: 'center',
					}}
				>
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<InfoCircleFilled
							style={{ fontSize: 48, color: '#1557BC', marginBottom: 16 }}
						/>
					</div>
					<div style={{ fontWeight: 600, fontSize: 18, marginBottom: 32 }}>
						Apakah anda ingin melakukan Cancel PO dengan detail berikut?
					</div>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
						<div>
							<Divider />

							{(() => {
								const record = data?.details.find(
									(detail) => detail.purchaseOrderId === POCancellationId,
								);
								return (
									<>
										<Row>
											<Col span={8}>No. PO</Col>
											<Col
												span={16}
												style={{
													display: 'flex',
													justifyContent: 'flex-start',
													fontWeight: 700,
												}}
											>
												: {record?.purchaseOrderNo}
											</Col>
										</Row>
										<Row>
											<Col span={8}>Merk Asset</Col>
											<Col
												span={16}
												style={{
													display: 'flex',
													justifyContent: 'flex-start',
													fontWeight: 700,
												}}
											>
												: {record?.brand}
											</Col>
										</Row>
									</>
								);
							})()}
							<Divider />

							<span>Silahkan input jumlah unit yang akan di cancel PO</span>

							<Divider style={{ opacity: 0 }} />

							<Row>
								<Col span={24}>
									<Form.Item
										name={fieldNameCancelPO('cancelledUnit')}
										label="Pilih Jumlah Unit"
										required
									>
										<Select
											options={Array.from(
												{
													length: Math.max(
														0,
														data?.details.find(
															(detail) =>
																detail.purchaseOrderId === POCancellationId,
														)?.remainingUnit || 0,
													),
												},
												(_, i) => ({ value: i + 1, label: String(i + 1) }),
											)}
										/>
									</Form.Item>
								</Col>
							</Row>
							<Divider />
							<Row>
								<Col span={24}>
									<Form.Item
										name={fieldNameCancelPO('notes')}
										label="Notes"
										required
									>
										<TextArea />
									</Form.Item>
								</Col>
							</Row>
						</div>
						<Divider style={{ opacity: 0, marginBottom: 48 }} />
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								gap: 16,
								width: '100%',
								marginBottom: 24,
							}}
						>
							<Button
								onClick={handleClosePOCancellationModal}
								type="default"
								style={{
									minWidth: 120,
									fontWeight: 600,
									fontSize: 16,
									borderRadius: 6,
									border: '1px solid #1557BC',
									color: '#1557BC',
									background: 'white',
								}}
							>
								BACK
							</Button>
							<Button
								htmlType="submit"
								type="primary"
								style={{
									minWidth: 120,
									fontWeight: 600,
									fontSize: 16,
									borderRadius: 6,
									background: '#1557BC',
									color: 'white',
									border: 'none',
								}}
								onClick={formCancelPO.submit}
							>
								SUBMIT
							</Button>
						</div>
					</div>
				</FleetModal>
				<Form.Item
					hidden
					name={fieldNameCancelPO('purchaseOrderId')}
				>
					<Input />
				</Form.Item>
			</Form>

			{/* PO REQUEST PENCAIRAN MODAL */}
			<FleetModal
				open={requestPencairanModalOpen} // Controlled by the new state
				maskClosable={true}
				width={600} // Increased width for better display of multiple items
				footer={null} // Custom footer for multiple buttons
				onCancel={handleCloseRequestPencairanModal}
				style={{
					padding: '36px 32px 24px 32px',
					borderRadius: 12,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					width: '100%',
					textAlign: 'center',
				}}
			>
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<InfoCircleFilled
						style={{ fontSize: 48, color: '#1557BC', marginBottom: 16 }}
					/>
				</div>
				<div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
					Apakah Anda ingin melakukan Request Pencairan untuk PO berikut?
				</div>
				<div
					style={{
						fontWeight: 400,
						fontSize: 14,
						marginBottom: 8,
						color: 'lightgray',
					}}
				>
					Berikut adalah detailnya:
				</div>
				<Divider /> {/* Adjust or remove if needed */}
				<div style={{ maxHeight: '300px', overflowY: 'auto', width: '100%' }}>
					{(() => {
						// Filter details to only include selected POs
						const selectedPoDetails = (data?.details || []).filter((detail) =>
							selectedPOs.includes(detail.purchaseOrderId),
						);

						if (selectedPoDetails.length === 0) {
							return <p>Tidak ada PO yang dipilih untuk Request Pencairan.</p>;
						}

						return selectedPoDetails.map((record, index) => (
							<div key={record.purchaseOrderId}>
								<Row>
									<Col
										span={8}
										style={{ textAlign: 'left' }}
									>
										No. PO
									</Col>
									<Col
										span={16}
										style={{
											display: 'flex',
											justifyContent: 'flex-start',
											fontWeight: 700,
										}}
									>
										: {record?.purchaseOrderNo}
									</Col>
								</Row>
								<Row>
									<Col
										span={8}
										style={{ textAlign: 'left' }}
									>
										Merk Asset
									</Col>
									<Col
										span={16}
										style={{
											display: 'flex',
											justifyContent: 'flex-start',
											fontWeight: 700,
										}}
									>
										: {record?.brand}
									</Col>
								</Row>
								<Row>
									<Col
										span={8}
										style={{ textAlign: 'left' }}
									>
										Jumlah Unit
									</Col>
									<Col
										span={16}
										style={{
											display: 'flex',
											justifyContent: 'flex-start',
											fontWeight: 700,
										}}
									>
										: {record?.totalUnit || 0}
									</Col>
								</Row>
								{/* Add a divider between multiple records if necessary */}
								{index < selectedPoDetails.length - 1 && (
									<Divider style={{ margin: '12px 0' }} />
								)}
							</div>
						));
					})()}
				</div>
				<Divider style={{ opacity: 0, marginBottom: 24 }} />
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						gap: 16,
						width: '100%',
						marginBottom: 24,
					}}
				>
					<Button
						onClick={handleCloseRequestPencairanModal}
						type="default"
						style={{
							minWidth: 120,
							fontWeight: 600,
							fontSize: 16,
							borderRadius: 6,
							border: '1px solid #1557BC',
							color: '#1557BC',
							background: 'white',
						}}
					>
						TIDAK
					</Button>
					<Button
						type="primary"
						style={{
							minWidth: 120,
							fontWeight: 600,
							fontSize: 16,
							borderRadius: 6,
							background: '#1557BC',
							color: 'white',
							border: 'none',
						}}
						onClick={submitDisbursementRequest}
					>
						YA
					</Button>
				</div>
			</FleetModal>

			{/* PO PRINT ALL MODAL */}
			<FleetModal
				open={printAllPurchaseOrderModal} // Controlled by the new state
				maskClosable={true}
				width={600} // Increased width for better display of multiple items
				footer={null} // Custom footer for multiple buttons
				onCancel={() => setPrintAllPurchaseOrderModal(false)}
				style={{
					padding: '36px 32px 24px 32px',
					borderRadius: 12,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					width: '100%',
					textAlign: 'center',
				}}
			>
				<div style={{ display: 'flex', justifyContent: 'center' }}>
					<InfoCircleFilled
						style={{ fontSize: 48, color: '#1557BC', marginBottom: 16 }}
					/>
				</div>
				<div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>
					Apakah Anda ingin melakukan Pencetakan untuk PO berikut?
				</div>
				<div
					style={{
						fontWeight: 400,
						fontSize: 14,
						marginBottom: 8,
						color: 'lightgray',
					}}
				>
					Berikut adalah detailnya:
				</div>
				<Divider /> {/* Adjust or remove if needed */}
				<div style={{ maxHeight: '300px', overflowY: 'auto', width: '100%' }}>
					{(() => {
						// Filter details to only include selected POs
						const selectedPoDetails = (data?.details || []).filter((detail) =>
							selectedPOs.includes(detail.purchaseOrderId),
						);

						if (selectedPoDetails.length === 0) {
							return <p>Tidak ada PO yang dipilih untuk Request Pencairan.</p>;
						}

						return selectedPoDetails.map((record, index) => (
							<div key={record.purchaseOrderId}>
								<Row>
									<Col
										span={8}
										style={{ textAlign: 'left' }}
									>
										No. PO
									</Col>
									<Col
										span={16}
										style={{
											display: 'flex',
											justifyContent: 'flex-start',
											fontWeight: 700,
										}}
									>
										: {record?.purchaseOrderNo}
									</Col>
								</Row>
								<Row>
									<Col
										span={8}
										style={{ textAlign: 'left' }}
									>
										Merk Asset
									</Col>
									<Col
										span={16}
										style={{
											display: 'flex',
											justifyContent: 'flex-start',
											fontWeight: 700,
										}}
									>
										: {record?.brand}
									</Col>
								</Row>
								<Row>
									<Col
										span={8}
										style={{ textAlign: 'left' }}
									>
										Jumlah Unit
									</Col>
									<Col
										span={16}
										style={{
											display: 'flex',
											justifyContent: 'flex-start',
											fontWeight: 700,
										}}
									>
										: {record?.totalUnit || 0}
									</Col>
								</Row>
								{/* Add a divider between multiple records if necessary */}
								{index < selectedPoDetails.length - 1 && (
									<Divider style={{ margin: '12px 0' }} />
								)}
							</div>
						));
					})()}
				</div>
				<Divider style={{ opacity: 0, marginBottom: 24 }} />
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						gap: 16,
						width: '100%',
						marginBottom: 24,
					}}
				>
					<Button
						onClick={() => setPrintAllPurchaseOrderModal(false)}
						type="default"
						style={{
							minWidth: 120,
							fontWeight: 600,
							fontSize: 16,
							borderRadius: 6,
							border: '1px solid #1557BC',
							color: '#1557BC',
							background: 'white',
						}}
					>
						TIDAK
					</Button>
					<Button
						type="primary"
						style={{
							minWidth: 120,
							fontWeight: 600,
							fontSize: 16,
							borderRadius: 6,
							background: '#1557BC',
							color: 'white',
							border: 'none',
						}}
						onClick={printAllPurchaseOrder}
					>
						YA
					</Button>
				</div>
			</FleetModal>

			<Space
				direction="vertical"
				style={{ display: 'flex' }}
				size={'large'}
			>
				<div
					style={{
						display: 'flex',
						gap: 10,
						marginBottom: 30,
					}}
				>
					<div>
						<button
							style={{
								display: 'flex',
								height: 44,
								width: 44,
								fontSize: 24,
								border: 'none',
								alignItems: 'center',
								justifyContent: 'center',
								background: '#1557BC',
								color: 'white',
								cursor: 'pointer',
							}}
							onClick={() => navigate('FRMPOListPage')}
						>
							<ArrowLeftOutlined />
						</button>
					</div>
					<div style={{ flex: 1 }}>
						<FleetTitle>Create Purchase Order (PO)</FleetTitle>
					</div>
				</div>
				<DataRow
					items={[
						[
							[
								'Nama Customer',
								data?.customerId ? (
									<a
										href="#"
										className="trigger-popup"
										onClick={(e) => {
											e.preventDefault();
											openCustomerDetail(data.customerId);
										}}
									>
										{data?.customerName}
									</a>
								) : (
									data?.customerName
								),
							],
							[
								'Group Customer BMPP',
								data?.bmppGroupId ? (
									<a
										href="#"
										className="trigger-popup"
										onClick={(e) => {
											e.preventDefault();
											openCustomerGroupDetail(data!.bmppGroupId);
										}}
									>
										{data?.bmppGroupName}
									</a>
								) : (
									'-'
								),
							],
						],
						[
							[
								'No. MPP',
								data?.mppNo ? (
									<a
										onClick={(e) => {
											e.preventDefault();
											if (mppId)
												navigate('MPPPage', {
													mppId: mppId,
													onBack: () => {
														navigate('FRMPOActionListPage', {
															mppId,
															mppNo,
															spfNo,
															mklNo,
														});
													},
													viewOnly: true,
												});
										}}
									>
										{data.mppNo}
									</a>
								) : (
									'-'
								),
							],
						],
					]}
					loading={isLoading}
				/>
				<div style={{ display: 'flex', gap: 8, width: '100%' }}>
					<Select
						style={{ minWidth: 160, width: '40%' }}
						value={searchBy}
						onChange={(value) => {
							if (value === 'poStatus') {
								setSearchValue(statusOptions[0].id);
							}
							setSearchValue('');
							setSearchBy(value);
						}}
						options={searchByOptions.map((opt) => ({
							label: opt.value,
							value: opt.id,
						}))}
						placeholder="Cari berdasarkan"
					/>
					{searchValueInput}
					<Button
						type="primary"
						onClick={handleSearch}
						style={{ width: 190 }}
					>
						SEARCH
					</Button>
				</div>
				<Table
					scroll={{ x: 'max-content' }}
					bordered
					loading={isLoading}
					dataSource={tableData}
					columns={[
						{ title: 'No', dataIndex: 'no', align: 'center', width: 1 },
						{
							title: 'No. Simulasi',
							dataIndex: 'creditSimulationNo',
							render: (v, record) => (
								<a
									onClick={(e) => {
										e.preventDefault();
										if (record.creditSimulationId) {
											navigate('SimulationPage', {
												simulationId: record.creditSimulationId,
												onBack: () => {
													navigate('FRMPOActionListPage', {
														mppId,
														mppNo,
														spfNo,
														mklNo,
													});
												},
												showResult: true,
												facilityId: record.facilityId,
												viewOnly: true,
												spfId: record.spfId,
											});
										}
									}}
									style={{ cursor: 'pointer' }}
								>
									{v}
								</a>
							),
						},
						{
							title: 'No. PO',
							dataIndex: 'noPo',
							render: (v, record) => (
								<a
									onClick={async (e) => {
										e.preventDefault();
										if (v && record.purchaseOrderId) {
											try {
												const response = await printPurchaseOrderMutate({
													purchaseOrderIds: [record.purchaseOrderId],
												});
												openDocumentModal(response);
											} catch (error) {
												console.error('Failed to load PO document:', error);
											}
										}
									}}
									style={{ cursor: v ? 'pointer' : 'default' }}
								>
									{v || '-'}
								</a>
							),
						},
						{ title: 'Supplier', dataIndex: 'supplier' },
						{ title: 'Merk Asset', dataIndex: 'merkAsset' },
						{
							title: 'Tanggal Terbit PO',
							dataIndex: 'tglTerbitPo',
							render: (v) => (v ? dayjs(v).format('DD-MM-YYYY') : '-'),
						},
						{
							title: 'Tanggal PO Expired',
							dataIndex: 'tglPoExpired',
							render: (v) => (v ? dayjs(v).format('DD-MM-YYYY') : '-'),
						},
						// {
						// 	title: 'Tanggal Perpanjangan PO',
						// 	dataIndex: 'tglPerpanjanganPo',
						// },
						{ title: 'Status PO', dataIndex: 'status' },
						{
							title: 'Jumlah Unit',
							dataIndex: 'totalUnit',
						},
						{
							title: 'Create PO',
							align: 'center',
							width: 100,
							render: (_, record) => {
								const enableColActions =
									// TODO: Disable for Dev!
									record.allowCreatePo && selectedPOs.length <= 0; // No rows selected

								return (
									<a
										onClick={
											enableColActions
												? (e) => {
														e.preventDefault();
														handleCreatePO(
															record.creditSimulationId,
															record.purchaseOrderId,
														);
													}
												: undefined
										}
										style={{ cursor: enableColActions ? 'pointer' : 'default' }}
									>
										<ArrowRightOutlined
											style={{
												color: enableColActions ? '#1557BC' : '#BCBCBC',
											}}
										/>
									</a>
								);
							},
						},

						{
							title: 'Select',
							align: 'center',
							width: 1,
							render: (_, record) => (
								<Checkbox
									// Add this line:
									checked={selectedPOs.includes(record.purchaseOrderId)}
									disabled={!record.allowSelect}
									onChange={(e) => {
										const checked = e.target.checked; // Get the checked state from the event object
										setSelectedPOs((prevSelectedPOs) => {
											const idToToggle = record.purchaseOrderId;
											const exists = prevSelectedPOs.includes(idToToggle);

											if (checked) {
												if (!exists) {
													return [...prevSelectedPOs, idToToggle];
												}
												return prevSelectedPOs;
											} else {
												if (exists) {
													return prevSelectedPOs.filter(
														(id) => id !== idToToggle,
													);
												}
												return prevSelectedPOs;
											}
										});
									}}
								/>
							),
						},
						{
							title: 'Retrieve',
							align: 'center',
							width: 1,
							render: (_, record) => {
								const enableColActions =
									record.allowRetrieve && selectedPOs.length <= 0; // No rows selected
								return (
									<a
										onClick={
											enableColActions
												? (e) => {
														e.preventDefault();
														handleRetrieve(record.purchaseOrderId);
													}
												: undefined
										}
										style={{ cursor: enableColActions ? 'pointer' : 'default' }}
									>
										<RetweetOutlined
											style={{
												color: enableColActions ? '#1557BC' : '#BCBCBC',
											}}
										/>
									</a>
								);
							},
						},
						{
							title: 'Extend/Cancel PO',
							align: 'center',
							width: 1,

							render: (_, record) => {
								const enableColActions =
									record.allowExtend && selectedPOs.length <= 0; // No rows selected

								return (
									<a
										onClick={
											enableColActions
												? (e) => {
														e.preventDefault();
														handleExtendCancelModal(record.purchaseOrderId);
													}
												: undefined
										}
										style={{ cursor: enableColActions ? 'pointer' : 'default' }}
									>
										<ArrowRightOutlined
											style={{
												color: enableColActions ? '#1557BC' : '#BCBCBC',
											}}
										/>
									</a>
								);
							},
						},
					]}
					pagination={{
						hideOnSinglePage: true,
						current: page,
						total: data?.details?.length || 0,
						pageSize,
						onChange: setPage,
					}}
					rowKey="key"
				/>
				{error && (
					<div style={{ color: 'red' }}>
						Gagal memuat detail purchase order.
					</div>
				)}
				<Row
					style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}
				>
					<Row style={{ gap: 23 }}>
						<Button
							style={{ width: 215 }}
							type="primary"
							disabled={selectedPOs.length <= 0}
							onClick={() => setPrintAllPurchaseOrderModal(true)}
						>
							PRINT PO
						</Button>
						<Button
							style={{ width: 215 }}
							type="primary"
							disabled={selectedPOs.length <= 0}
							onClick={() => setRequestPencairanModalOpen(true)}
						>
							REQUEST PENCAIRAN
						</Button>
					</Row>
				</Row>
			</Space>

			{/* Simulation Result Modal */}
			<SimulationResultModal
				open={simulationModalOpen}
				onClose={handleCloseSimulationModal}
				simulationId={selectedSimulation?.simulationId}
			/>
		</>
	);
};

export default FRMPOActionListPage;
